import { useMqttSubscription } from '@hooks/mqtt/useMqttSubscription';
import { DataPoint, DataPointWithValue } from '@src/types/DataPoint';
import { IMqttMessage } from '@src/types/Mqtt';
import {
  calculateDataPointValue,
  createDataPointsMap,
} from '@utils/dataPoints';
import { useCallback, useMemo } from 'react';

type MqttValue = IMqttMessage['value'];

function convertTimestamp(
  timestamp: number | string | undefined,
): string | undefined {
  if (timestamp === undefined) {
    return undefined;
  }
  if (typeof timestamp === 'string') {
    return timestamp;
  }
  return new Date(timestamp).toISOString();
}

function extractValue(value: unknown): MqttValue | undefined {
  if (value === null) {
    return null;
  }
  if (typeof value === 'string' || typeof value === 'number') {
    return value;
  }
  return undefined;
}

export function useDataPointsSubscription(
  dataPoints: DataPoint[],
): DataPointWithValue[] {
  const topics = useMemo(
    () => dataPoints.filter((dp) => dp.fullTopic).map((dp) => dp.fullTopic),
    [dataPoints],
  );

  const { data } = useMqttSubscription<MqttValue>(topics, {
    enabled: topics.length > 0,
  });

  const dataPointsWithValues = useMemo((): DataPointWithValue[] => {
    return dataPoints.map((dp) => ({
      ...dp,
      value: extractValue(data[dp.fullTopic]?.value),
      ts: convertTimestamp(data[dp.fullTopic]?.timestamp),
    }));
  }, [dataPoints, data]);

  const findDataPointByAlias = useCallback(
    (pointAlias: string) =>
      dataPointsWithValues.find((point) => point.pointAlias === pointAlias),
    [dataPointsWithValues],
  );

  const dataPointsWithCalculatedValues = useMemo((): DataPointWithValue[] => {
    const dataPointsMap = createDataPointsMap(dataPointsWithValues);

    return dataPoints.map((dataPoint) => {
      const existingDataPoint = dataPointsMap.get(dataPoint.fullTopic);

      if (!dataPoint.valueGenerationFunction) {
        return (
          existingDataPoint ?? {
            ...dataPoint,
            value: undefined,
            ts: undefined,
          }
        );
      }

      const calculationResult = calculateDataPointValue(
        dataPoint,
        findDataPointByAlias,
      );

      if (calculationResult) {
        return {
          ...dataPoint,
          value: calculationResult.value,
          ts: calculationResult.timestamp,
        };
      }

      return (
        existingDataPoint ?? {
          ...dataPoint,
          value: undefined,
          ts: undefined,
        }
      );
    });
  }, [dataPoints, dataPointsWithValues, findDataPointByAlias]);

  return dataPointsWithCalculatedValues;
}
